import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

/*  !! chemin corrigé ici !!  */
import { CartContext } from '../context/CartContext';

import AppBar        from '@mui/material/AppBar';
import Toolbar       from '@mui/material/Toolbar';
import Typography    from '@mui/material/Typography';
import Button        from '@mui/material/Button';
import IconButton    from '@mui/material/IconButton';
import Badge         from '@mui/material/Badge';
import Menu          from '@mui/material/Menu';
import MenuItem      from '@mui/material/MenuItem';
import Drawer        from '@mui/material/Drawer';
import List          from '@mui/material/List';
import ListItem      from '@mui/material/ListItem';
import ListItemText  from '@mui/material/ListItemText';
import useMediaQuery from '@mui/material/useMediaQuery';

import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MenuIcon         from '@mui/icons-material/Menu';
import HomeIcon         from '@mui/icons-material/Home';

import { LIFE_EVENTS, CARTES_PATH } from '../utils/lifeEvents';
import './Header.css';

/* ------------------------------------------------------------------ *
 *  PARAMÈTRES AUTH
 * ------------------------------------------------------------------ */
const PASSWORD_KEY   = 'admin_access';     // même couple clef/valeur que PasswordProtect
const PASSWORD_VALUE = 'enzo10';

/* ------------------------------------------------------------------ *
 *  UTILS
 * ------------------------------------------------------------------ */
const isAuthenticated = () => {
  const saved = localStorage.getItem(PASSWORD_KEY);
  if (!saved) return false;

  try {
    const { value, expires } = JSON.parse(saved);
    if (new Date() > new Date(expires)) {
      localStorage.removeItem(PASSWORD_KEY);
      return false;
    }
    return value === PASSWORD_VALUE;
  } catch {
    return false;
  }
};

/* ------------------------------------------------------------------ *
 *  COMPOSANT
 * ------------------------------------------------------------------ */
const Header = ({ cartIconRef }) => {
  /* ------------ State & context ----------------------------------- */
  const { cart }   = useContext(CartContext);

  const [drawerOpen, setDrawerOpen]   = useState(false);

  const [adminAnchor, setAdminAnc] = useState(null);       // sous-menu “Admin”
  const [eventMenu, setEventMenu] = useState(null);        // menu déroulant ouvert : { key, anchor }
  const [isAdmin,    setIsAdmin]   = useState(isAuthenticated());

  // Un menu par événement : la barre est plus large, le tiroir prend le relais plus tôt.
  const isMobile = useMediaQuery('(max-width:1250px)');

  /* ------------ Effets -------------------------------------------- */
  useEffect(() => { setIsAdmin(isAuthenticated()); }, []);
  // Navigation par événement de vie (un menu déroulant par événement, lifeEvents.js), puis
  // « Cartes à gratter » (l'ancien « Tous les visuels » : le catalogue des cartes), FAQ, Contact.

  /* ------------ Handlers ------------------------------------------ */
  const toggleDrawer = (open) => (evt) => {
    if (evt.type === 'keydown' && (evt.key === 'Tab' || evt.key === 'Shift'))
      return;
    setDrawerOpen(open);
  };

  /* Menu Admin */
  const openAdminMenu  = (e) => setAdminAnc(e.currentTarget);
  const closeAdminMenu = () => setAdminAnc(null);

  /* ---------------------------------------------------------------- *
   *  RENDU
   * ---------------------------------------------------------------- */
  const drawerContent = (
    <>
      <List>
        <ListItem button component={Link} to="/">
          <HomeIcon fontSize="small" sx={{ marginRight: 1 }} />
          <ListItemText primary="Accueil" />
        </ListItem>
        {LIFE_EVENTS.map((event) => (
          <React.Fragment key={event.key}>
            <ListItem button component={Link} to={event.path}>
              <ListItemText primary={event.label} primaryTypographyProps={{ sx: { fontWeight: 700 } }} />
            </ListItem>
            {event.menu.map((item) => (
              <ListItem button component={Link} to={item.to} key={item.to} sx={{ paddingLeft: 4, paddingTop: 0.25, paddingBottom: 0.25 }}>
                <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
          </React.Fragment>
        ))}
        <ListItem button component={Link} to={CARTES_PATH}>
          <ListItemText primary="Toutes les cartes à gratter" primaryTypographyProps={{ sx: { fontWeight: 700 } }} />
        </ListItem>
        <ListItem button component={Link} to="/faire-part-et-invitations-animes">
          <ListItemText primary="Tous les faire-part animés" primaryTypographyProps={{ sx: { fontWeight: 700 } }} />
        </ListItem>
        <ListItem button component={Link} to="/carte-a-gratter-digitale-questions-frequentes">
          <ListItemText primary="FAQ" />
        </ListItem>
        <ListItem button component={Link} to="/contact">
          <ListItemText primary="Contact" />
        </ListItem>

        {isAdmin && (
          <>
            <ListItem button component={Link} to="/gestion-commandes">
              <ListItemText primary="Gest. Commandes" />
            </ListItem>
            <ListItem button component={Link} to="/gestion-catalogue">
              <ListItemText primary="Gest. Produits/Catégories" />
            </ListItem>
            <ListItem button component={Link} to="/bibliotheque-visuels">
              <ListItemText primary="Bibliothèque de visuels" />
            </ListItem>
            <ListItem button component={Link} to="/gestion-promos">
              <ListItemText primary="Gest. Codes Promo" />
            </ListItem>
            <ListItem button component={Link} to="/creer-card">
              <ListItemText primary="Créer une carte" />
            </ListItem>
            <ListItem button component={Link} to="/list-cards">
              <ListItemText primary="Liste cartes" />
            </ListItem>
            <ListItem button component={Link} to="/tunnel-acquisition">
              <ListItemText primary="Tunnel d'acquisition" />
            </ListItem>
            <ListItem button component={Link} to="/trackers">
              <ListItemText primary="Trackers" />
            </ListItem>
          </>
        )}
      </List>
    </>
  );

  /* ---------------- JSX ------------------------------------------- */
  return (
    <AppBar
      position="sticky"
      elevation={0}
      component={motion.header}
      initial={{ opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      sx={{
        background: 'rgba(196,143,139,0.94)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderBottom: '1px solid rgba(255,255,255,0.18)',
        boxShadow: '0 2px 20px rgba(196,143,139,0.18)',
        padding: '6px 20px',
        top: 0,
        zIndex: 1100,
      }}
    >
      <Toolbar>
        <Typography
          variant="h6"
          component={Link}
          to="/"
          sx={{
            flexGrow: 1,
            textDecoration: 'none',
            fontSize: 30,
            color: 'white',
            fontFamily: 'Neucha, cursive',
          }}
        >
          <img src="/logoH.jpg" alt="Joli Ptit Mot" style={{ height: 80 }} />{' '}
        </Typography>

        {/* =================== NAV MOBILE =================== */}
        {isMobile ? (
          <>
            <IconButton edge="end" color="inherit" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
              <div role="presentation" onClick={toggleDrawer(false)} onKeyDown={toggleDrawer(false)}>
                {drawerContent}
              </div>
            </Drawer>
          </>
        ) : (
          /* =================== NAV DESKTOP =================== */
          <>
            <IconButton color="inherit" component={Link} to="/" aria-label="Accueil" title="Accueil">
              <HomeIcon />
            </IconButton>
            {/* Un menu par événement de vie (lifeEvents.js). */}
            {LIFE_EVENTS.map((event) => (
              <Button
                key={event.key}
                color="inherit"
                aria-haspopup="true"
                aria-expanded={eventMenu?.key === event.key}
                onClick={(e) => setEventMenu({ key: event.key, anchor: e.currentTarget })}
              >
                {event.label} ▾
              </Button>
            ))}
            {LIFE_EVENTS.map((event) => (
              <Menu
                key={event.key}
                anchorEl={eventMenu?.key === event.key ? eventMenu.anchor : null}
                open={eventMenu?.key === event.key}
                onClose={() => setEventMenu(null)}
              >
                {event.menu.map((item) => (
                  <MenuItem key={item.to} component={Link} to={item.to} onClick={() => setEventMenu(null)} sx={{ display: 'block' }}>
                    {item.label}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>{item.hint}</Typography>
                  </MenuItem>
                ))}
                <MenuItem component={Link} to={event.path} onClick={() => setEventMenu(null)} sx={{ borderTop: '1px solid rgba(0,0,0,0.08)', fontWeight: 600 }}>
                  {event.menuTout} →
                </MenuItem>
              </Menu>
            ))}
            <Button color="inherit" component={Link} to={CARTES_PATH}>
              Cartes à gratter
            </Button>
            <Button color="inherit" component={Link} to="/carte-a-gratter-digitale-questions-frequentes">
              FAQ
            </Button>
            <Button color="inherit" component={Link} to="/contact">
              Contact
            </Button>

            {/* -------- Sous-menu ADMIN -------- */}
            {isAdmin && (
              <>
                <Button color="inherit" className="btnMenuAdmin" onClick={openAdminMenu}>
                  Admin
                </Button>
                <Menu
                  anchorEl={adminAnchor}
                  open={Boolean(adminAnchor)}
                  onClose={closeAdminMenu}
                >
                  <MenuItem component={Link} to="/gestion-commandes" onClick={closeAdminMenu}>
                    Gest. Commandes
                  </MenuItem>
                  <MenuItem component={Link} to="/gestion-catalogue" onClick={closeAdminMenu}>
                    Gest. Produits/Catégories
                  </MenuItem>
                  <MenuItem component={Link} to="/bibliotheque-visuels" onClick={closeAdminMenu}>
                    Bibliothèque de visuels
                  </MenuItem>
                  <MenuItem component={Link} to="/gestion-promos" onClick={closeAdminMenu}>
                    Gest. Codes Promo
                  </MenuItem>
                  <MenuItem component={Link} to="/creer-card" onClick={closeAdminMenu}>
                    Créer une carte
                  </MenuItem>
                  <MenuItem component={Link} to="/list-cards" onClick={closeAdminMenu}>
                    Liste cartes
                  </MenuItem>
                  <MenuItem component={Link} to="/tunnel-acquisition" onClick={closeAdminMenu}>
                    Tunnel d'acquisition
                  </MenuItem>
                  <MenuItem component={Link} to="/trackers" onClick={closeAdminMenu}>
                    Trackers
                  </MenuItem>
                </Menu>
              </>
            )}
          </>
        )}

        {/* Panier */}
        <IconButton color="inherit" component={Link} to="/cart" ref={cartIconRef}>
          <Badge badgeContent={cart.length} color="secondary">
            <ShoppingCartIcon />
          </Badge>
        </IconButton>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
